(() => {
  const SELECTOR_MENU_ROOT = 'full-menu .thb-full-menu, .full-menu .thb-full-menu';
  const SELECTOR_MENU_ITEM = 'li.menu-item-has-children';
  const SELECTOR_TRIGGER = `${SELECTOR_MENU_ITEM} > a.thb-full-menu--link`;

  function setExpanded(trigger, expanded) {
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    trigger.setAttribute('aria-haspopup', 'true');
  }

  function closeItem(menuItem) {
    if (!menuItem) return;

    menuItem.classList.remove('is-open');
    const trigger = menuItem.querySelector(':scope > a.thb-full-menu--link');
    setExpanded(trigger, false);

    menuItem.querySelectorAll(`${SELECTOR_MENU_ITEM}.is-open`).forEach((child) => {
      child.classList.remove('is-open');
      const childTrigger = child.querySelector(':scope > a.thb-full-menu--link');
      setExpanded(childTrigger, false);
    });
  }

  function closeAll(menuRoot, exceptItems = []) {
    const exceptSet = new Set(exceptItems.filter(Boolean));
    menuRoot.querySelectorAll(`${SELECTOR_MENU_ITEM}.is-open`).forEach((item) => {
      if (exceptSet.has(item)) return;
      closeItem(item);
    });
  }

  function getAncestorMenuItems(menuItem) {
    const ancestors = [];
    let current = menuItem;
    while (current) {
      if (current.matches?.(SELECTOR_MENU_ITEM)) ancestors.push(current);
      current = current.parentElement?.closest?.(SELECTOR_MENU_ITEM) || null;
    }
    return ancestors;
  }

  function initMenu(menuRoot) {
    const triggers = menuRoot.querySelectorAll(SELECTOR_TRIGGER);
    if (!triggers.length) return;

    triggers.forEach((trigger) => {
      const menuItem = trigger.parentElement;
      const submenu = menuItem?.querySelector?.(':scope > .sub-menu');
      if (!menuItem || !submenu) return;

      setExpanded(trigger, false);

      trigger.addEventListener('click', (e) => {
        const isOpen = menuItem.classList.contains('is-open');

        // First click opens dropdown; second click follows the link.
        if (!isOpen) {
          e.preventDefault();

          const keepOpen = getAncestorMenuItems(menuItem);
          closeAll(menuRoot, keepOpen);

          menuItem.classList.add('is-open');
          setExpanded(trigger, true);
        }
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;

        const isOpen = menuItem.classList.contains('is-open');
        if (!isOpen) {
          e.preventDefault();

          const keepOpen = getAncestorMenuItems(menuItem);
          closeAll(menuRoot, keepOpen);

          menuItem.classList.add('is-open');
          setExpanded(trigger, true);
        }
      });
    });

    document.addEventListener('click', (e) => {
      if (!(e.target instanceof Node)) return;
      if (menuRoot.contains(e.target)) return;
      closeAll(menuRoot);
    });

    document.addEventListener('keyup', (e) => {
      if (e.key !== 'Escape') return;
      closeAll(menuRoot);
    });
  }

  function init() {
    document.querySelectorAll(SELECTOR_MENU_ROOT).forEach(initMenu);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

(() => {
  const INNER_SELECTOR = '.section-collapsible-content .collapsible-content__inner';
  const DETAILS_SELECTOR = 'details';
  const CONTENT_SELECTOR = '.collapsible__content';

  function getBasePaddingBottomPx(element) {
    const prevInline = element.style.paddingBottom;
    if (prevInline) element.style.paddingBottom = '';
    const computed = window.getComputedStyle(element).paddingBottom;
    const base = Number.parseFloat(computed) || 0;
    if (prevInline) element.style.paddingBottom = prevInline;
    return base;
  }

  function setPaddingBottomPx(element, value) {
    const safe = Math.max(0, Math.ceil(value));
    element.style.paddingBottom = `${safe}px`;
  }

  function getOpenContentOverflowBottomPx(inner) {
    // Measure how far opened absolute content extends beyond the bottom of the inner container.
    // This avoids over-allocating space when the absolute content overlaps the summary.
    const prevInlinePaddingBottom = inner.style.paddingBottom;
    if (prevInlinePaddingBottom) inner.style.paddingBottom = '';

    const innerRect = inner.getBoundingClientRect();
    let maxOverflow = 0;

    inner.querySelectorAll(`${DETAILS_SELECTOR}[open]`).forEach((details) => {
      const content = details.querySelector(CONTENT_SELECTOR);
      if (!(content instanceof HTMLElement)) return;

      const rect = content.getBoundingClientRect();
      const overflow = rect.bottom - innerRect.bottom;
      if (overflow > maxOverflow) maxOverflow = overflow;
    });

    if (prevInlinePaddingBottom) inner.style.paddingBottom = prevInlinePaddingBottom;
    return Math.max(0, maxOverflow);
  }

  function updateInnerPadding(inner) {
    if (!inner?.isConnected) return;
    const basePadding = getBasePaddingBottomPx(inner);
    const extra = getOpenContentOverflowBottomPx(inner);
    setPaddingBottomPx(inner, basePadding + extra);
  }

  function scheduleUpdate(inner) {
    if (!inner) return;
    if (inner.__absOverflowRaf) window.cancelAnimationFrame(inner.__absOverflowRaf);
    inner.__absOverflowRaf = window.requestAnimationFrame(() => {
      inner.__absOverflowRaf = 0;
      updateInnerPadding(inner);
    });
  }

  function initInner(inner) {
    if (!(inner instanceof HTMLElement)) return;
    if (inner.dataset.absOverflowInit === 'true') return;
    inner.dataset.absOverflowInit = 'true';

    const update = () => scheduleUpdate(inner);
    const updateAfterAnimation = () => window.setTimeout(update, 450);

    // Details toggle can be triggered either by the browser default behavior
    // or programmatically by the theme's <collapsible-row> element.
    inner.querySelectorAll(DETAILS_SELECTOR).forEach((details) => {
      details.addEventListener('toggle', update);
      // When the theme intercepts clicks to run its own animation, the toggle event
      // can be delayed; schedule another update after the animation finishes.
      details.addEventListener('toggle', updateAfterAnimation);
      details.addEventListener('click', (e) => {
        if (!(e.target instanceof Element)) return;
        if (!e.target.closest('summary')) return;
        update();
        updateAfterAnimation();
      }, true);
    });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(update);
      inner.querySelectorAll(CONTENT_SELECTOR).forEach((content) => ro.observe(content));
      inner.__absOverflowResizeObserver = ro;
    }

    window.addEventListener('resize', update, { passive: true });
    // Run twice to catch late layout (fonts/images) and the theme's initial accordion state.
    update();
    window.setTimeout(update, 0);
    window.setTimeout(update, 500);
  }

  function init() {
    document.querySelectorAll(INNER_SELECTOR).forEach(initInner);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor: sections can be re-rendered without full page load.
  document.addEventListener('shopify:section:load', init);
})();
